/* eslint-disable react-hooks/rules-of-hooks */
import type { Session } from 'next-auth';
import {
  SessionContextValue,
  getSession,
  signOut,
  useSession
} from 'next-auth/react';
import {
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import LineLogInApi from '../api/backend/LineLogInApi';
import { EventBus, EventBusService } from '../services/EventBusService';
import { useRouter } from 'next/router';
import { deleteTokenCookie } from '../utils/sessionCookie';
import backendFetch, { TokenExpiredError } from '../utils/backendFetch';
import type { Liff } from '@line/liff';
import useContextState from '../hooks/useContextState';
import { useDebounceEffect } from '../hooks/useDebounceEffect';
import { ErrorHandlingProxy } from '../utils/errorHandling.proxy';
import { clearExpiredIdToken } from '../utils/liff';
import { ConnectUserType } from '../constants';
import { AuthProvider, LoginProvider } from '../constants/auth';

type AuthType = {
  status: 'authenticated';
  update: (data?: any) => Promise<Session | null>;
  data: Omit<Session, 'user'> & {
    loginProvider: LoginProvider;
    provider: AuthProvider;
    idToken: string | null;
    accessToken: string | null;
    profile: string | null;
    user: Session['user'] & { profile: string | null };
  };
};
const SessionContext = createContext<SessionContextValue | AuthType>({
  status: 'loading',
  data: null,
  update: () => new Promise(() => {})
});
const AppQueryContext = createContext<Record<string, string | undefined>>({});
const LiffContext = createContext<Liff | undefined>(undefined);
const ErrorHandlerContext = createContext<
  | {
      proxy: ReturnType<typeof ErrorHandlingProxy>;
      createProxy: (service: object) => typeof service;
      onError: Parameters<typeof ErrorHandlingProxy>[0];
    }
  | undefined
>(undefined);

const SessionActionContext = createContext<
  | EventBus<{
      close: () => void;
    }>
  | undefined
>(undefined);

const InternalSessionProvider: FC<
  PropsWithChildren<{
    useLocalSession?: boolean;
    noLogin?: boolean;
  }>
> = ({ children, useLocalSession, noLogin }) => {
  const [session, setSession] = useContextState(SessionContext, {
    status: 'loading',
    data: null,
    update: () => new Promise(() => {})
  });
  const [liff, setLiff] = useState<Liff>();
  const { query } = useRouter();
  const [appQuery, setAppQuery] = useState<Record<string, string | undefined>>(
    {}
  );
  const [reloadSession, setReloadSession] = useState(false);
  useEffect(() => {
    if (query['liff.state']) {
      setAppQuery(
        Array.from(
          new URL(`https://test${query['liff.state']}`).searchParams.entries()
        ).reduce((prev, [key, value]) => ({ ...prev, [key]: value }), {})
      );
    } else {
      setAppQuery(
        Object.entries(query).reduce(
          (prev, [key, value]) => ({
            ...prev,
            [key]: Array.isArray(value) ? value.join(',') : value
          }),
          {}
        )
      );
    }
  }, [query]);

  const eventBus = useMemo(
    () =>
      new EventBusService().createEventBus<{
        close: () => void;
      }>('SessionActions'),
    []
  );
  const onError = (error: unknown) => {
    error instanceof TokenExpiredError && setReloadSession(true);
  };

  useEffect(() => {
    reloadSession &&
      session.update(session.data).then(() => window.location.reload());
  }, [reloadSession]);
  const errorHandlerProxy = ErrorHandlingProxy(onError);
  const errorHandling = {
    proxy: errorHandlerProxy,
    onError,
    createProxy: (service: object) =>
      new Proxy<typeof service>(service, errorHandlerProxy)
  };
  useEffect(() => {
    (async () => {
      if (useLocalSession) {
        // ローカルの時のみ事前に用意したLINEアカウントに設定する
        if (process.env.NODE_ENV === 'development') {
          const refreshToken = process.env.NEXT_PUBLIC_LINE_REFRESH_TOKEN || '';
          const query = new URLSearchParams({
            grant_type: 'refresh_token',
            refresh_token: refreshToken,
            client_id: process.env.NEXT_PUBLIC_LINE_CLIENT_ID || '',
            client_secret: process.env.NEXT_PUBLIC_LINE_CLIENT_SECRET || ''
          });

          const response = await fetch(
            `https://api.line.me/oauth2/v2.1/token`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              },
              body: query
            }
          ).then((res) => res.json());
          const liffAccessToken = response?.access_token;
          if (liffAccessToken) {
            const res = await backendFetch('lineLogInApi', {
              method: 'GET',
              headers: {
                'x-ath-lineaccesstoken': liffAccessToken
              }
            });
            setLiff({} as unknown as Liff);
            setSession({
              status: 'authenticated',
              data: {
                loginProvider: 'line',
                idToken: res.result.idToken,
                accessToken: res.result.accessToken,
                expires: '',
                user: {
                  email: 'test@athome.co.jp',
                  name: 'test',
                  image: '',
                  profile: res.result.chimeId
                }
              },
              update: () => new Promise(() => {})
            });
          }
        } else {
          import('@line/liff').then((liffClient) => {
            const liff = liffClient as unknown as Liff;
            setLiff(liff);
            clearExpiredIdToken(process.env.NEXT_PUBLIC_LIFF_ID!);
            liff
              .init({ liffId: process.env.NEXT_PUBLIC_LIFF_ID! })
              .then(async () => {
                const getLoginInfo = noLogin
                  ? new Promise<{
                      idToken: string | null;
                      chimeId: string | null;
                      accessToken: string | null;
                    }>((resolve) =>
                      resolve({
                        idToken: liff.getIDToken(),
                        accessToken: liff.getAccessToken(),
                        chimeId: null
                      })
                    )
                  : new LineLogInApi(liff.getIDToken()!).login();
                eventBus.on('close', () => liff.closeWindow());
                if (liff.isInClient()) {
                  Promise.all([liff.getProfile(), getLoginInfo]).then(
                    ([profile, { idToken, chimeId, accessToken }]) => {
                      setSession({
                        status: 'authenticated',
                        data: {
                          loginProvider: 'line',
                          idToken,
                          accessToken,
                          expires: '',
                          user: {
                            email: profile.userId,
                            name: profile.displayName,
                            image: profile.pictureUrl,
                            profile: chimeId
                          }
                        },
                        update: () => new Promise(() => {})
                      });
                      return profile;
                    }
                  );
                } else {
                  throw 'Not in LIFF Client';
                }
              })
              .catch((error) => {
                console.error(error);
                setSession({
                  status: 'unauthenticated',
                  data: null,
                  update: () => new Promise(() => {})
                });
              });
          });
        }
      }
    })();
  }, [useLocalSession]);
  if (!useLocalSession) {
    const authSession = useSession({ required: true });
    'newUser' in authSession && signOut();
    useDebounceEffect(
      () => {
        setSession(
          authSession?.data
            ? {
                ...authSession,
                data: {
                  ...authSession.data,
                  expires: authSession.data.expires,
                  loginProvider: 'cognito'
                }
              }
            : authSession
        );
      },
      0,
      [authSession]
    );
  }
  useEffect(() => {
    if (session.data) {
      backendFetch(
        'issuer/init',
        { method: 'GET' },
        (session.data as unknown as { idToken: string }).idToken,
        false,
        true
      );
    } else {
      deleteTokenCookie();
    }
  }, [session]);
  return (
    <LiffContext.Provider value={liff}>
      <AppQueryContext.Provider value={appQuery}>
        <SessionActionContext.Provider value={eventBus}>
          <SessionContext.Provider value={session}>
            <ErrorHandlerContext.Provider value={errorHandling}>
              {children}
            </ErrorHandlerContext.Provider>
          </SessionContext.Provider>
        </SessionActionContext.Provider>
      </AppQueryContext.Provider>
    </LiffContext.Provider>
  );
};

export function useInternalSession(): SessionContextValue | AuthType;
export function useInternalSession(assumeStatus: 'authenticated'): AuthType;
export function useInternalSession(assumeStatus?: 'authenticated') {
  const session = useContext(SessionContext);
  if (assumeStatus) {
    if (assumeStatus === session.status) {
      return session;
    } else {
      throw new Error('ステータス不適切');
    }
  }
  return session;
}
export function useSessionAction() {
  return useContext(SessionActionContext);
}
export function useAppQuery() {
  return useContext(AppQueryContext);
}
export function useLiff() {
  return useContext(LiffContext);
}
export function useErrorHandler() {
  return useContext(ErrorHandlerContext);
}
export function useMember() {
  const user = useInternalSession()?.data?.user as Session['user'] & {
    profile: string;
    companyId?: string;
    kaiinId?: string;
    companyName?: string;
    tel?: string;
    id: string;
    createdAt: number;
    userType?: ConnectUserType;
  };
  return {
    member: {
      id: user?.id,
      userId: user?.profile,
      username: user?.name || user?.email,
      email: user?.email,
      companyId: user?.companyId,
      companyName: user?.companyName,
      tel: user?.tel,
      createdAt: user?.createdAt,
      kaiinId: user?.kaiinId,
      image: user?.image,
      userType: (user?.userType || '') as ConnectUserType
    }
  };
}

export default InternalSessionProvider;
